/**
 * プライベートメールアドレスの濫用監視 `/private-addresses`。
 *
 * `apps/api/src/routes/ops/private-addresses.ts` の 4 本には**画面が 1 つも
 * 無かった**。結果として
 *   - 組織別の発行状況・発行速度・明細を運営が一度も見られない
 *   - `POST /private-addresses/abuse-scan` を叩く経路が人にも機械にも無く、
 *     `abuse_reports` への自動起票（`dedupe_key` 接頭辞
 *     `private-address-rate-`）が**一度も走らない**
 * という状態だった。レート制限（60 件/時）が最後の砦として効いているぶん
 * 即座の被害は小さいが、乗っ取られたトークンによる継続的な乱発を検知する
 * 層が実質不在になっていた。
 *
 * ## 3 つのタブと、その並び順の理由
 *
 *   - **発行速度**（既定）… 濫用の判定は**総数ではなく速度**で行う。
 *     使い捨てアドレスは作って捨てるものなので、プラン上限（＝生存数）に
 *     一度も触れずに名前空間を汚し続けられる。総数の上位は「よく使って
 *     いる優良顧客」かもしれず、一次判断に使えない。
 *   - **発行状況**（stats）… 焼却率と迷惑メール率を分けて読むための集計。
 *     **焼却率だけが高い組織は濫用ではなく被害者**の可能性が高い
 *     （渡した相手から漏らされて捨てている）。
 *   - **明細** … `organizationId` を選んだときだけ引ける。全組織横断で
 *     明細を並べる画面は**作らない**（「誰にどのアドレスを渡したか」は
 *     契約者の取引先一覧そのもので、運営が日常的に眺めてよい情報ではない）。
 *
 * 閾値（1 時間 40 件 / 24 時間 150 件）は画面に持たない。サーバーが
 * `thresholds` と `reason` を返すので、そのまま出す。**片方だけ変えると
 * 「画面は赤いのに通報が立たない」が起きる**ため、判定は
 * `privateAddressAbuseReason()` の 1 か所に閉じてある。
 */
import * as React from 'react';
import { Link, getRouteApi } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Gauge, ListTree, ScanSearch, Table2 } from 'lucide-react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  CursorPagination,
  DataTable,
  PageHeader,
  Section,
  StatCard,
  StatusBadge,
  formatCount,
  formatDateTime,
  formatPercent,
  formatRelative,
  type DataTableColumn,
} from '@mail-sv/ui';
import { labelOf } from '@mail-sv/shared';
import { api } from '../lib/api.js';
import { qk } from '../lib/query-keys.js';
import { useCursorList } from '../lib/use-cursor-list.js';
import { OPS_PERMISSION } from '../lib/permissions.js';
import { usePermission } from '../stores/auth.js';
import { PageTabs } from '../components/page-tabs.js';
import { NativeSelect } from '../components/form-fields.js';
import {
  ListEmpty,
  PermissionDenied,
  QueryError,
  RefetchingDot,
  errorMessageOf,
} from '../components/states.js';
import { RID } from './ids.js';
import type {
  PrivateAddressRow,
  PrivateAddressStatsRow,
  PrivateAddressVelocityRow,
} from '../types/api.js';

const routeApi = getRouteApi(RID.privateAddresses);

interface VelocityResponse {
  thresholds: { burstPerHour: number; perDay: number };
  organizations: PrivateAddressVelocityRow[];
}

export function PrivateAddressesPage(): React.ReactElement {
  const search = routeApi.useSearch();
  const navigate = routeApi.useNavigate();
  const canRead = usePermission(OPS_PERMISSION.securityRead);

  if (!canRead) return <PermissionDenied />;

  return (
    <>
      <PageHeader
        title="プライベートメールアドレス"
        description="渡す相手ごとの使い捨て転送アドレスの発行状況。運営が見るのは濫用であって中身ではありません（本文も宛先の名簿もこの画面には出ません）。"
      />

      <PageTabs
        value={search.tab}
        onChange={(tab) => void navigate({ search: (prev) => ({ ...prev, tab }) })}
        tabs={[
          { value: 'velocity', label: '発行速度', icon: Gauge },
          { value: 'stats', label: '発行状況', icon: Table2 },
          { value: 'detail', label: '明細', icon: ListTree },
        ]}
      />

      {search.tab === 'velocity' ? <VelocityTab /> : null}
      {search.tab === 'stats' ? <StatsTab /> : null}
      {search.tab === 'detail' ? <DetailTab /> : null}
    </>
  );
}

/* ------------------------------------------------------------- 発行速度 */

function VelocityTab(): React.ReactElement {
  const qc = useQueryClient();
  const navigate = routeApi.useNavigate();
  const canWrite = usePermission(OPS_PERMISSION.securityWrite);
  const [scanned, setScanned] = React.useState<{ scanned: number; flagged: number } | null>(null);

  const query = useQuery({
    queryKey: qk.privateAddressVelocity(),
    queryFn: () => api.get<VelocityResponse>('/private-addresses/velocity'),
  });

  /*
   * **起票は人が押す。停止はしない。** 乱発は乗っ取りのこともあれば正当な
   * 自動化（申込フォームごとに 1 本発行する等）のこともあるので、
   * 自動停止すると正当な使い方をしている契約者のメールが運営の判断を挟まず
   * に止まる。ここは `abuse_reports` に起票するところまで。
   */
  const scan = useMutation({
    mutationFn: () =>
      api.post<{ scanned: number; flagged: Array<{ organizationId: string; reason: string }> }>(
        '/private-addresses/abuse-scan',
        {},
      ),
    onSuccess: (res) => {
      setScanned({ scanned: res.scanned, flagged: res.flagged.length });
      void qc.invalidateQueries({ queryKey: ['ops', 'private-addresses'] });
      void qc.invalidateQueries({ queryKey: ['ops', 'security', 'abuse'] });
    },
  });

  const rows = query.data?.organizations ?? [];
  const thresholds = query.data?.thresholds;
  const abusive = rows.filter((r) => r.abusive);

  const columns: Array<DataTableColumn<PrivateAddressVelocityRow>> = [
    {
      id: 'org',
      header: '契約組織',
      cell: (r) => (
        <Link
          to="/organizations/$orgId"
          params={{ orgId: r.organizationId }}
          search={{ tab: 'overview' }}
          className="font-mono text-xs hover:text-primary"
        >
          {r.organizationId}
        </Link>
      ),
    },
    {
      id: 'abusive',
      header: '判定',
      cell: (r) =>
        r.abusive ? (
          <Badge variant="destructive" size="sm">
            閾値超過
          </Badge>
        ) : (
          <Badge variant="muted" size="sm">
            通常
          </Badge>
        ),
    },
    { id: 'lastHour', header: '直近 1 時間', align: 'right', cell: (r) => formatCount(r.lastHour) },
    { id: 'lastDay', header: '直近 24 時間', align: 'right', cell: (r) => formatCount(r.lastDay) },
    {
      id: 'viaApi',
      header: 'うち API 経由',
      align: 'right',
      hideBelow: 'md',
      // `mst_*` トークン経由の比率は「自動化されているか」の手掛かり。
      cell: (r) => formatCount(r.lastDayViaApi),
    },
    {
      id: 'disabled',
      header: '24 時間で停止',
      align: 'right',
      hideBelow: 'md',
      cell: (r) => formatCount(r.lastDayDisabled),
    },
    { id: 'live', header: '生存数', align: 'right', hideBelow: 'lg', cell: (r) => formatCount(r.liveTotal) },
    {
      id: 'reason',
      header: '理由',
      hideBelow: 'lg',
      cell: (r) => (r.reason ? <span className="text-xs text-destructive">{r.reason}</span> : '—'),
    },
    {
      id: 'actions',
      header: <span className="sr-only">操作</span>,
      align: 'right',
      width: 'w-24',
      cell: (r) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() =>
            void navigate({
              search: (prev) => ({ ...prev, tab: 'detail', organizationId: r.organizationId }),
            })
          }
        >
          明細
        </Button>
      ),
    },
  ];

  return (
    <>
      <div className="mb-3 flex items-center justify-end gap-2">
        <RefetchingDot active={query.isFetching && !query.isLoading} />
        {canWrite ? (
          <Button size="sm" onClick={() => scan.mutate()} loading={scan.isPending}>
            <ScanSearch aria-hidden />
            濫用スキャンを実行
          </Button>
        ) : null}
      </div>

      <Alert className="mb-4">
        <AlertTitle>判定は「総数」ではなく「発行速度」で行います</AlertTitle>
        <AlertDescription>
          使い捨てアドレスは作って捨てるものなので、プランの上限（生存数）に一度も触れずに
          名前空間を汚し続けられます。閾値は
          {thresholds ? ` 1 時間 ${thresholds.burstPerHour} 件 / 24 時間 ${thresholds.perDay} 件` : '（読み込み中）'}
          で、超えた組織は「濫用スキャンを実行」で
          <Link to="/security" search={{ tab: 'abuse' }} className="mx-1 text-primary hover:underline">
            迷惑行為の通報
          </Link>
          に起票されます（<strong>送信は止めません</strong>。止めるかどうかは人が決めます）。
        </AlertDescription>
      </Alert>

      {scan.isError ? (
        <Alert variant="destructive" className="mb-4">
          <AlertTitle>濫用スキャンに失敗しました</AlertTitle>
          {/* `String(error)` だと封筒の中身が出ないので、共通の整形を通す。 */}
          <AlertDescription>{errorMessageOf(scan.error)}</AlertDescription>
        </Alert>
      ) : null}

      {scanned ? (
        <Alert variant={scanned.flagged > 0 ? 'warning' : 'default'} className="mb-4">
          <AlertTitle>
            {formatCount(scanned.scanned)} 組織を検査し、{formatCount(scanned.flagged)} 組織を起票しました
          </AlertTitle>
          <AlertDescription>
            同じ組織で既に開いている通報があれば、行は増やさず発生回数と最終時刻だけを進めます
            （本当に見るべき 1 件が埋もれないようにするため）。
          </AlertDescription>
        </Alert>
      ) : null}

      {abusive.length > 0 ? (
        <Alert variant="destructive" className="mb-4">
          <AlertTitle>{formatCount(abusive.length)} 組織が閾値を超えています</AlertTitle>
          <AlertDescription>
            乗っ取られたトークンによる乱発の可能性があります。ただし申込フォームごとに 1 本発行する
            ような正当な自動化でも超えます。明細と焼却率を見てから判断してください。
          </AlertDescription>
        </Alert>
      ) : null}

      {query.isError ? (
        <QueryError error={query.error} onRetry={() => void query.refetch()} className="mb-4" />
      ) : null}

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(r) => r.organizationId}
        loading={query.isLoading}
        empty={<ListEmpty filtered={false} entityLabel="発行の記録" />}
      />
    </>
  );
}

/* ------------------------------------------------------------- 発行状況 */

function StatsTab(): React.ReactElement {
  const navigate = routeApi.useNavigate();
  const query = useQuery({
    queryKey: qk.privateAddressStats(),
    queryFn: () => api.get<{ organizations: PrivateAddressStatsRow[] }>('/private-addresses/stats'),
  });

  const rows = query.data?.organizations ?? [];
  const totals = rows.reduce(
    (acc, r) => ({
      total: acc.total + r.total,
      active: acc.active + r.active,
      burned: acc.burned + r.burned,
      viaApi: acc.viaApi + r.createdViaApi,
    }),
    { total: 0, active: 0, burned: 0, viaApi: 0 },
  );

  const columns: Array<DataTableColumn<PrivateAddressStatsRow>> = [
    {
      id: 'org',
      header: '契約組織',
      cell: (r) => (
        <Link
          to="/organizations/$orgId"
          params={{ orgId: r.organizationId }}
          search={{ tab: 'overview' }}
          className="font-medium hover:text-primary"
        >
          {r.displayName ?? r.organizationId}
        </Link>
      ),
    },
    { id: 'total', header: '発行数', align: 'right', cell: (r) => formatCount(r.total) },
    { id: 'active', header: '有効', align: 'right', hideBelow: 'md', cell: (r) => formatCount(r.active) },
    { id: 'burned', header: '破棄', align: 'right', hideBelow: 'md', cell: (r) => formatCount(r.burned) },
    {
      id: 'burnRate',
      header: '焼却率',
      align: 'right',
      /*
       * **焼却率が高いだけの組織は「被害者」**（渡した相手から漏らされて
       * 捨てている）。濫用の指標として読まないよう、色は付けない。
       */
      cell: (r) => <span className="tabular">{formatPercent(r.burnRate, 1)}</span>,
    },
    {
      id: 'spamRate',
      header: '迷惑メール率',
      align: 'right',
      hideBelow: 'md',
      cell: (r) => <span className="tabular">{formatPercent(r.spamRate, 1)}</span>,
    },
    {
      id: 'viaApi',
      header: 'API 発行',
      align: 'right',
      hideBelow: 'lg',
      cell: (r) => formatCount(r.createdViaApi),
    },
    {
      id: 'lastCreatedAt',
      header: '最終発行',
      hideBelow: 'lg',
      cell: (r) => (r.lastCreatedAt ? formatRelative(r.lastCreatedAt) : '—'),
    },
    {
      id: 'actions',
      header: <span className="sr-only">操作</span>,
      align: 'right',
      width: 'w-24',
      cell: (r) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() =>
            void navigate({
              search: (prev) => ({ ...prev, tab: 'detail', organizationId: r.organizationId }),
            })
          }
        >
          明細
        </Button>
      ),
    },
  ];

  return (
    <>
      <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-4">
        <StatCard label="発行済み" value={formatCount(totals.total)} unit="件" />
        <StatCard label="有効" value={formatCount(totals.active)} unit="件" />
        <StatCard
          label="漏洩により破棄"
          value={formatCount(totals.burned)}
          unit="件"
          hint="渡した相手から漏らされて捨てられたもの。濫用の指標ではありません。"
        />
        <StatCard label="API 経由の発行" value={formatCount(totals.viaApi)} unit="件" />
      </div>

      <div className="mb-3 flex items-center justify-end gap-2">
        <RefetchingDot active={query.isFetching && !query.isLoading} />
      </div>

      {query.isError ? (
        <QueryError error={query.error} onRetry={() => void query.refetch()} className="mb-4" />
      ) : null}

      <Section
        title="組織別の発行状況"
        description="発行数の多い順に最大 200 組織。焼却率（破棄 ÷ 発行）と迷惑メール率（迷惑 ÷ 受信）は分けて読みます。"
      >
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(r) => r.organizationId}
          loading={query.isLoading}
          empty={<ListEmpty filtered={false} entityLabel="発行の記録" />}
        />
      </Section>
    </>
  );
}

/* ----------------------------------------------------------------- 明細 */

function DetailTab(): React.ReactElement {
  const search = routeApi.useSearch();
  const navigate = routeApi.useNavigate();

  // 組織の候補は集計から作る。1 件も発行していない組織を選んでも意味が無い。
  const stats = useQuery({
    queryKey: qk.privateAddressStats(),
    queryFn: () => api.get<{ organizations: PrivateAddressStatsRow[] }>('/private-addresses/stats'),
  });

  const organizationId = search.organizationId ?? '';

  /*
   * **`organizationId` はサーバー側で必須**（`z.string().min(1)`）。
   * 未選択のまま引くと必ず 400 になるので `enabled` で止める。
   */
  const list = useCursorList<PrivateAddressRow>({
    path: '/private-addresses',
    keyFor: (params) => qk.privateAddresses(params),
    filters: { organizationId, status: search.status },
    enabled: organizationId.length > 0,
  });

  const columns: Array<DataTableColumn<PrivateAddressRow>> = [
    { id: 'address', header: 'アドレス', cell: (r) => <span className="font-mono text-xs">{r.address}</span> },
    {
      id: 'label',
      header: 'ラベル',
      // ラベルは「誰に渡したか」。**発行時に必須**（無いと漏洩元を辿れない）。
      cell: (r) => r.label ?? '—',
    },
    { id: 'status', header: '状態', cell: (r) => <StatusBadge status={r.status} size="sm" /> },
    {
      id: 'createdVia',
      header: '発行経路',
      hideBelow: 'md',
      cell: (r) => (
        <Badge variant={r.createdVia === 'api' ? 'warning' : 'muted'} size="sm">
          {labelOf(r.createdVia)}
        </Badge>
      ),
    },
    { id: 'received', header: '受信', align: 'right', hideBelow: 'md', cell: (r) => formatCount(r.receivedCount) },
    { id: 'spam', header: '迷惑', align: 'right', hideBelow: 'md', cell: (r) => formatCount(r.spamCount) },
    {
      id: 'lastReceivedAt',
      header: '最終受信',
      hideBelow: 'lg',
      cell: (r) => (r.lastReceivedAt ? formatRelative(r.lastReceivedAt) : '未受信'),
    },
    { id: 'createdAt', header: '発行', hideBelow: 'lg', cell: (r) => formatDateTime(r.createdAt) },
  ];

  const options = [
    { value: '', label: '組織を選択してください' },
    ...(stats.data?.organizations ?? []).map((r) => ({
      value: r.organizationId,
      label: `${r.displayName ?? r.organizationId}（${r.total} 件）`,
    })),
  ];

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <span className="text-xs text-muted-foreground">契約組織</span>
        <div className="w-80">
          <NativeSelect
            value={organizationId}
            onChange={(v) =>
              void navigate({ search: (prev) => ({ ...prev, organizationId: v || undefined }) })
            }
            options={options}
          />
        </div>
        <div className="w-40">
          <NativeSelect
            value={search.status ?? ''}
            onChange={(v) =>
              void navigate({
                search: (prev) => ({
                  ...prev,
                  status: v === '' ? undefined : (v as typeof prev.status),
                }),
              })
            }
            options={[
              { value: '', label: 'すべての状態' },
              { value: 'active', label: labelOf('active') },
              { value: 'disabled', label: labelOf('disabled') },
              { value: 'burned', label: labelOf('burned') },
            ]}
          />
        </div>
        <RefetchingDot active={list.query.isFetching && !list.query.isLoading} />
      </div>

      <Alert className="mb-4">
        <AlertTitle>明細は 1 組織ずつしか引けません</AlertTitle>
        <AlertDescription>
          「誰にどのアドレスを渡したか」は契約者の取引先の一覧そのものです。全組織を横断して
          並べる画面は作っていません（API 側も <code className="font-mono">organizationId</code> を必須にしています）。
          ここに出るのはラベルとメモまでで、本文も宛先の名簿もありません。
        </AlertDescription>
      </Alert>

      {organizationId.length === 0 ? (
        <ListEmpty filtered={false} entityLabel="表示する明細" />
      ) : (
        <>
          {list.query.isError ? (
            <QueryError error={list.query.error} onRetry={() => void list.query.refetch()} className="mb-4" />
          ) : null}
          <DataTable
            columns={columns}
            rows={list.rows}
            rowKey={(r) => r.id}
            loading={list.query.isLoading}
            empty={<ListEmpty filtered={search.status !== undefined} entityLabel="プライベートアドレス" />}
            footer={
              <CursorPagination
                nextCursor={list.nextCursor}
                canGoPrev={list.canGoPrev}
                onNext={list.goNext}
                onPrev={list.goPrev}
                limit={list.limit}
                onLimitChange={list.setLimit}
                currentCount={list.rows.length}
                loading={list.query.isFetching}
              />
            }
          />
        </>
      )}
    </>
  );
}
